import { Link } from "react-router-dom";
import { Clock, Timer, Compass, Home, Moon, Briefcase, PawPrint, Pill, CalendarHeart } from "lucide-react";

const dogWalking = [
  { icon: Clock, label: "15-min Potty Break", price: "$12", desc: "A quick stretch, sniff, and bathroom break." },
  { icon: Timer, label: "30-min Walk", price: "$20", desc: "A proper neighborhood loop with sniffs and treats." },
  { icon: Compass, label: "60-min Adventure Walk", price: "$35", desc: "Longer trails, new smells, full body wags." },
];

const petSitting = [
  { icon: Home, label: "Drop-in Visit", price: "$15", sub: "per time, per day", desc: "Feeding, water, playtime, potty, cuddles." },
  { icon: Moon, label: "Overnight Stay", price: "$75", desc: "Cozy company through the night, in your home." },
  { icon: Briefcase, label: "Vacation Care Package", price: "from $50/day", desc: "Full-day care while you travel — pets stay home." },
];

const addons = [
  { icon: PawPrint, label: "Additional Pet", price: "+$5" },
  { icon: Pill, label: "Medication Administration", price: "+$5" },
  { icon: CalendarHeart, label: "Holiday Visits", price: "+20%" },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <header className="text-center max-w-2xl mx-auto mb-16">
          <span className="font-script text-2xl text-[#b85b7e]">our services</span>
          <h2
            data-testid="services-title"
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-[#3F2A56] mt-2 leading-tight"
          >
            Tail-wagging care, every visit.
          </h2>
          <p className="mt-5 text-[#5C4A73] text-lg leading-relaxed">
            From quick potty breaks to overnight stays, every service is built
            around your pet&rsquo;s personality, routine, and comfort.
          </p>
        </header>

        <div className="grid md:grid-cols-2 gap-8">
          <ServiceColumn title="Dog Walking" accent="#3F2A56" items={dogWalking} testid="dog-walking" />
          <ServiceColumn title="Pet Sitting" accent="#b85b7e" items={petSitting} testid="pet-sitting" />
        </div>

        <div className="mt-14 parchment-card rounded-3xl p-8 sm:p-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-7">
            <div>
              <span className="font-script text-xl text-[#4A6741]">make it extra cozy</span>
              <h3 className="font-display font-bold text-3xl text-[#3F2A56]">Add-ons</h3>
            </div>
            <Link
              to="/book"
              data-testid="addons-book-cta"
              className="rounded-full bg-[#3F2A56] hover:bg-[#523870] text-[#F4E8C8] font-semibold px-6 py-3 text-sm transition-transform hover:-translate-y-0.5"
            >
              Build your booking →
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {addons.map((a) => (
              <div
                key={a.label}
                data-testid={`addon-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center gap-4 p-4 rounded-2xl bg-[#F4E8C8]/60 border border-[#3F2A56]/15"
              >
                <div className="w-11 h-11 rounded-full bg-[#3F2A56] text-[#F4E8C8] grid place-items-center shrink-0">
                  <a.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold text-[#3F2A56] leading-tight">
                    {a.label}
                  </div>
                  <div className="font-script text-xl text-[#b85b7e]">{a.price}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <Perk
            title="Household services included!"
            body="While caring for your pets, I can also help keep your home happy and secure — mail & packages, plants, trash to the curb, alternating lights & blinds, and more."
          />
          <Perk
            title="Free Meet & Greet!"
            body="Let's meet so you (and your pets!) feel completely comfortable before the first visit. Building trust, one tail wag at a time."
            accent
          />
        </div>
      </div>
    </section>
  );
}

function ServiceColumn({ title, accent, items, testid }) {
  return (
    <div className="parchment-card rounded-3xl overflow-hidden">
      <div
        className="px-7 py-4 font-display font-bold text-2xl text-[#F4E8C8] flex items-center justify-between"
        style={{ background: accent }}
      >
        <span>{title}</span>
        <PawPrint className="w-5 h-5 opacity-80" />
      </div>
      <div className="divide-y divide-[#3F2A56]/10">
        {items.map((s) => (
          <div
            key={s.label}
            data-testid={`service-${testid}-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="px-7 py-6 flex gap-5 hover:bg-[#F4E8C8]/40 transition-colors"
          >
            <div
              className="w-14 h-14 rounded-2xl grid place-items-center shrink-0"
              style={{ background: accent, color: "#F4E8C8" }}
            >
              <s.icon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <h4 className="font-display text-xl font-semibold text-[#3F2A56]">
                  {s.label}
                </h4>
                <span className="font-display text-2xl font-bold" style={{ color: accent }}>
                  {s.price}
                </span>
              </div>
              {s.sub && (
                <div className="text-xs italic text-[#5C4A73] -mt-1">{s.sub}</div>
              )}
              <p className="text-[#5C4A73] mt-1.5 leading-relaxed">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Perk({ title, body, accent }) {
  return (
    <div
      data-testid={`perk-${title.toLowerCase().split(" ")[0]}`}
      className={`rounded-3xl p-7 border-2 ${
        accent
          ? "bg-[#E8F0E6] border-[#4A6741]/30"
          : "bg-[#FFFBF0] border-[#3F2A56]/15"
      }`}
    >
      <h4 className="font-display text-2xl font-bold text-[#3F2A56] mb-2">
        {title}
      </h4>
      <p className="text-[#5C4A73] leading-relaxed">{body}</p>
    </div>
  );
}
